import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import {
  FilterInput,
  LogicalOperator,
  type FilterGroup,
} from '@/components/publicSearch/constants';
import type { SearchType } from '@/hooks/publicSearch/useSearchType';
import {
  DETAIL_ANIMAL_SEARCH_TYPES,
  SEARCH_TYPE,
} from '@/constants/searchType';

const MAX_SAVED_FILTERS = 20;

export interface SavedFilter {
  id: string;
  searchType?: SearchType;
  name: string;
  filters: FilterInput[];
  searchTerm: string;
  saleDateFilter?: string | null;
  saleTypeFilterValues?: string[];
  gridSortModel: GridSortModel;
  isAiMode: boolean;
  filterGroups: FilterGroup[];
  groupsOperator: string;
  aiSearchPrompt: string;
  createdAt: number;
}

export interface SavedFilterDraft {
  searchType: SearchType;
  name: string;
  filters: FilterInput[];
  searchTerm: string;
  saleDateFilter?: string | null;
  saleTypeFilterValues?: string[];
  gridSortModel: GridSortModel;
  isAiMode: boolean;
  filterGroups: FilterGroup[];
  groupsOperator: string;
  aiSearchPrompt: string;
}

interface SavedFiltersStore {
  savedFilters: SavedFilter[];
  getSavedFilters: (searchType: SearchType) => SavedFilter[];
  saveFilter: (filter: SavedFilterDraft) => SavedFilter;
  updateFilter: (
    searchType: SearchType,
    id: string,
    updates: Partial<SavedFilterDraft>,
  ) => SavedFilter | null;
  deleteFilter: (searchType: SearchType, id: string) => void;
  clearAllFilters: (searchType: SearchType) => void;
}

export function supportsSavedFilters(searchType: SearchType): boolean {
  return (
    searchType === SEARCH_TYPE.ANIMAL ||
    searchType === SEARCH_TYPE.MEMBERS ||
    DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType)
  );
}

function normalizeSaleTypeValues(values?: string[]) {
  return [...(values ?? [])].sort();
}

function filtersAreEqual(a: FilterInput[], b: FilterInput[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) =>
    `${x.field}|${x.operator}|${x.value}`.localeCompare(
      `${y.field}|${y.operator}|${y.value}`,
    ),
  );
  const sortedB = [...b].sort((x, y) =>
    `${x.field}|${x.operator}|${x.value}`.localeCompare(
      `${y.field}|${y.operator}|${y.value}`,
    ),
  );
  return sortedA.every(
    (f, i) =>
      f.field === sortedB[i]!.field &&
      f.operator === sortedB[i]!.operator &&
      f.value === sortedB[i]!.value,
  );
}

function gridSortModelEqual(a: GridSortModel, b: GridSortModel): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => x.field === b[i]?.field && x.sort === b[i]?.sort);
}

function filterGroupsEqual(a: FilterGroup[], b: FilterGroup[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((g, i) => {
    const o = b[i];
    if (!o || g.logicalOperator !== o.logicalOperator) return false;
    return filtersAreEqual(g.filters, o.filters);
  });
}

function savedFilterHasGroupedFilters(x: { filterGroups?: FilterGroup[] }) {
  return (x.filterGroups ?? []).some((g) => g.filters.length > 0);
}

export function savedFiltersAreEquivalent(
  filter: SavedFilter,
  draft: Omit<SavedFilterDraft, 'name'>,
): boolean {
  const savedGrouped = savedFilterHasGroupedFilters(filter);
  const draftGrouped = savedFilterHasGroupedFilters(draft);
  if (savedGrouped && draftGrouped) {
    return (
      filter.searchType === draft.searchType &&
      filtersAreEqual(filter.filters, draft.filters) &&
      (filter.saleDateFilter ?? null) === (draft.saleDateFilter ?? null) &&
      normalizeSaleTypeValues(filter.saleTypeFilterValues).join('|') ===
        normalizeSaleTypeValues(draft.saleTypeFilterValues).join('|') &&
      gridSortModelEqual(
        filter.gridSortModel ?? [],
        draft.gridSortModel ?? [],
      ) &&
      filterGroupsEqual(filter.filterGroups ?? [], draft.filterGroups ?? []) &&
      (filter.groupsOperator ?? LogicalOperator.or) ===
        (draft.groupsOperator ?? LogicalOperator.or)
    );
  }
  return (
    filter.searchType === draft.searchType &&
    filter.searchTerm === draft.searchTerm &&
    filtersAreEqual(filter.filters, draft.filters) &&
    (filter.saleDateFilter ?? null) === (draft.saleDateFilter ?? null) &&
    normalizeSaleTypeValues(filter.saleTypeFilterValues).join('|') ===
      normalizeSaleTypeValues(draft.saleTypeFilterValues).join('|') &&
    gridSortModelEqual(filter.gridSortModel ?? [], draft.gridSortModel ?? []) &&
    (filter.isAiMode ?? false) === (draft.isAiMode ?? false) &&
    filterGroupsEqual(filter.filterGroups ?? [], draft.filterGroups ?? []) &&
    (filter.groupsOperator ?? LogicalOperator.or) ===
      (draft.groupsOperator ?? LogicalOperator.or) &&
    (filter.aiSearchPrompt ?? '') === (draft.aiSearchPrompt ?? '')
  );
}

const useSavedFiltersStore = create<SavedFiltersStore>()(
  persist(
    (set, get) => ({
      savedFilters: [],

      getSavedFilters: (searchType) =>
        get().savedFilters.filter(
          (filter) => !filter.searchType || filter.searchType === searchType,
        ),

      saveFilter: (filter) => {
        const id = Date.now().toString();
        const newFilter: SavedFilter = {
          id,
          searchType: filter.searchType,
          name:
            filter.name.trim() ||
            `Filter ${get().getSavedFilters(filter.searchType).length + 1}`,
          filters: filter.filters,
          searchTerm: filter.searchTerm,
          saleDateFilter: filter.saleDateFilter ?? null,
          saleTypeFilterValues: filter.saleTypeFilterValues ?? [],
          gridSortModel: filter.gridSortModel ?? [],
          isAiMode: filter.isAiMode ?? false,
          filterGroups: filter.filterGroups ?? [],
          groupsOperator: filter.groupsOperator ?? LogicalOperator.or,
          aiSearchPrompt: filter.aiSearchPrompt ?? '',
          createdAt: Date.now(),
        };

        set((state) => {
          const matchingFilters = state.savedFilters.filter(
            (savedFilter) =>
              savedFilter.searchType === filter.searchType ||
              !savedFilter.searchType,
          );
          const nonMatchingFilters = state.savedFilters.filter(
            (savedFilter) =>
              savedFilter.searchType &&
              savedFilter.searchType !== filter.searchType,
          );

          const updatedFilters = [newFilter, ...matchingFilters].slice(
            0,
            MAX_SAVED_FILTERS,
          );

          return {
            savedFilters: [...updatedFilters, ...nonMatchingFilters],
          };
        });

        return newFilter;
      },

      updateFilter: (searchType, id, updates) => {
        let updatedFilter: SavedFilter | null = null;
        set((state) => ({
          savedFilters: state.savedFilters.map((filter) => {
            if (
              filter.id !== id ||
              (filter.searchType && filter.searchType !== searchType)
            ) {
              return filter;
            }

            updatedFilter = {
              ...filter,
              searchType,
              name: updates.name?.trim() || filter.name,
              filters: updates.filters ?? filter.filters,
              searchTerm: updates.searchTerm ?? filter.searchTerm,
              saleDateFilter:
                updates.saleDateFilter === undefined
                  ? (filter.saleDateFilter ?? null)
                  : updates.saleDateFilter,
              saleTypeFilterValues:
                updates.saleTypeFilterValues ??
                filter.saleTypeFilterValues ??
                [],
              gridSortModel: updates.gridSortModel ?? filter.gridSortModel,
              isAiMode: updates.isAiMode ?? filter.isAiMode,
              filterGroups: updates.filterGroups ?? filter.filterGroups,
              groupsOperator: updates.groupsOperator ?? filter.groupsOperator,
              aiSearchPrompt: updates.aiSearchPrompt ?? filter.aiSearchPrompt,
            };
            return updatedFilter;
          }),
        }));

        return updatedFilter;
      },

      deleteFilter: (searchType, id) => {
        set((state) => ({
          savedFilters: state.savedFilters.filter(
            (filter) =>
              filter.id !== id ||
              (filter.searchType && filter.searchType !== searchType),
          ),
        }));
      },

      clearAllFilters: (searchType) => {
        set((state) => ({
          savedFilters: state.savedFilters.filter(
            (filter) => filter.searchType && filter.searchType !== searchType,
          ),
        }));
      },
    }),
    {
      name: 'SavedFiltersStore',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export default useSavedFiltersStore;
